import React from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';

/**
 * MapControls — vertical floating button stack pinned to the top-
 * right of the map (Apple Photos / Maps pattern).
 *
 * Five buttons:
 *   +  zoom in one level
 *   −  zoom out one level
 *   🗺️ map style cycle (Standard ↔ Satellite)
 *   ⌖  locate me (drop blue dot at user GPS)
 *   ⇲  fit all markers (auto-zoom to bbox)
 *
 * Web-only — relies on Leaflet's imperative API from the parent.
 * Native fallback renders nothing.
 */

export interface MapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCycleStyle: () => void;
  /** Current style ID — drives the icon variant in the button.
   *  We only show one icon at a time; tap cycles to the next. */
  styleId: 'standard' | 'satellite';
  onLocateMe: () => void;
  onFitAll: () => void;
  /** True while locate-me is requesting permission / GPS. */
  locating?: boolean;
}

export const MapControls: React.FC<MapControlsProps> = ({
  onZoomIn,
  onZoomOut,
  onCycleStyle,
  styleId,
  onLocateMe,
  onFitAll,
  locating,
}) => {
  return (
    <View style={styles.stack} pointerEvents="box-none" testID="map-controls">
      <ControlButton
        icon="add"
        onPress={onZoomIn}
        accessibilityLabel="Zoom in"
        testID="map-zoom-in"
      />
      <ControlButton
        icon="remove"
        onPress={onZoomOut}
        accessibilityLabel="Zoom out"
        testID="map-zoom-out"
      />
      <View style={styles.divider} />
      <ControlButton
        icon={styleId === 'satellite' ? 'satellite' : 'map'}
        onPress={onCycleStyle}
        accessibilityLabel={
          styleId === 'satellite'
            ? 'Switch to standard map'
            : 'Switch to satellite map'
        }
        testID="map-style-cycle"
      />
      <ControlButton
        icon={locating ? 'my-location' : 'near-me'}
        onPress={onLocateMe}
        accessibilityLabel="Locate me"
        testID="map-locate-me"
        active={locating}
      />
      <ControlButton
        icon="zoom-out-map"
        onPress={onFitAll}
        accessibilityLabel="Fit all markers"
        testID="map-fit-all"
      />
    </View>
  );
};

interface ControlButtonProps {
  icon: keyof typeof MaterialIcons.glyphMap;
  onPress: () => void;
  accessibilityLabel: string;
  testID?: string;
  active?: boolean;
}

const ControlButton: React.FC<ControlButtonProps> = ({
  icon,
  onPress,
  accessibilityLabel,
  testID,
  active,
}) => (
  <Pressable
    onPress={onPress}
    hitSlop={6}
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel}
    testID={testID}
    style={({ pressed }) => [
      styles.btn,
      active && styles.btnActive,
      pressed && { opacity: 0.85 },
    ]}
  >
    <MaterialIcons
      name={icon}
      size={20}
      color={active ? fpColors.onPrimary : fpColors.onSurface}
    />
  </Pressable>
);

const styles = StyleSheet.create({
  stack: {
    position: 'absolute' as any,
    top: Platform.OS === 'ios' ? 120 : 88,
    right: 12,
    zIndex: 1000,
    gap: 8,
    alignItems: 'center',
  },
  btn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.95)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 2px 8px rgba(0,0,0,0.25)' } as any)
      : null),
  },
  btnActive: {
    backgroundColor: fpColors.primary,
  },
  divider: {
    width: 24,
    height: 1,
    backgroundColor: 'rgba(0,0,0,0.15)',
    marginVertical: 2,
  },
});

export default MapControls;
